// Phase 61: Per-account running balance widget.
// Uses computeAccountBalances(transactions, accounts) from lib/accounts.js.
// Renders each non-zero account with running balance, with a total at the
// bottom. Tap to navigate to Transactions with the account pre-filtered.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../i18n/index.jsx';
import { accountLabel, accountTypeMeta, computeAccountBalances } from '../../lib/accounts.js';
import { formatMoney } from '../../lib/format.js';
import { DashboardAccountDonut } from '../DashboardCharts.jsx';

export default function AccountBalancesWidget({ transactions, accounts, currency }) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  // Phase 61: default expanded if ≤ 3 accounts, collapsed otherwise.
  const [expanded, setExpanded] = useState((accounts || []).length <= 3);
  // Phase 62: toggle for the donut chart visualization.
  // Rules of Hooks (Phase 49o): every hook must be declared BEFORE any
  //   conditional return. Calling useState AFTER `return null` would
  //   violate the rules → React crashes the whole tree silently.
  const [showDonut, setShowDonut] = useState(false);

  if (!accounts || accounts.length === 0) return null;

  const balances = computeAccountBalances(transactions || [], accounts);
  // Sort: accounts with balance ≠ 0 first, by balance desc; then zero-balance.
  const items = accounts
    .filter(a => !a.deleted)
    .map(a => {
      const meta = accountTypeMeta(a.type);
      return {
        id: a.local_uuid || a.id,
        name: accountLabel(a, locale) || a.name,
        icon: a.icon || meta.icon,
        color: a.color || meta.color,
        balance: balances.get(a.local_uuid || a.id) || 0,
      };
    })
    .sort((a, b) => Math.abs(b.balance) - Math.abs(a.balance) || (a.name > b.name ? 1 : -1));
  const total = items.reduce((s, i) => s + i.balance, 0);
  const visible = expanded ? items : items.slice(0, 3);
  const hidden = items.length - visible.length;
  const positiveTotal = items.reduce((s, i) => s + Math.max(0, i.balance), 0);
  const donutData = items.map(d => ({
    ...d,
    pct: positiveTotal > 0 ? Math.max(0, d.balance) / positiveTotal : 0,
  }));

  return (
    <div
      className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-3 sm:p-4 mb-4"
      data-testid="account-balances-widget"
    >
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between w-full text-left mb-2"
        data-testid="account-balances-toggle"
        aria-expanded={expanded}
      >
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
          💼 {t('accounts.title')} ({items.length})
        </h3>
        <div className="flex items-center gap-2">
          <span
            className={[
              'text-sm font-semibold tabular-nums',
              total >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-600 dark:text-red-400',
            ].join(' ')}
            data-testid="account-balances-total"
          >
            {formatMoney(total, currency)}
          </span>
          <span className="text-slate-400 dark:text-slate-500 text-xs">
            {expanded ? '▾' : '▸'}
          </span>
        </div>
      </button>

      <ul className="space-y-1" data-testid="account-balances-list">
        {visible.map(item => (
          <li key={item.id}>
            <button
              type="button"
              onClick={() => navigate(`/transactions?account_local_uuid=${encodeURIComponent(item.id)}`)}
              className="w-full flex items-center justify-between px-2 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 min-h-[44px]"
              data-testid={`account-balance-${item.id}`}
              aria-label={`${item.name}: ${formatMoney(item.balance, currency)}`}
            >
              <span className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                <span aria-hidden="true">{item.icon}</span>
                <span>{item.name}</span>
              </span>
              <span
                className={[
                  'text-sm font-medium tabular-nums',
                  item.balance >= 0
                    ? 'text-emerald-700 dark:text-emerald-300'
                    : 'text-red-600 dark:text-red-400',
                ].join(' ')}
              >
                {formatMoney(item.balance, currency)}
              </span>
            </button>
          </li>
        ))}
      </ul>

      {!expanded && hidden > 0 && (
        <p className="text-xs text-slate-400 dark:text-slate-500 mt-1 ml-2">
          +{hidden} {t('accounts.all').toLowerCase()}…
        </p>
      )}

      {/* Phase 62: toggle to view as a donut chart */}
      {items.length >= 2 && (
        <button
          type="button"
          onClick={() => setShowDonut(!showDonut)}
          className="mt-2 text-xs text-brand-600 dark:text-brand-300 hover:underline px-2 py-1 min-h-[32px]"
          data-testid="account-donut-toggle"
        >
          {showDonut ? t('common.close') : t('chart.viewAsChart')} 📊
        </button>
      )}

      {showDonut && (
        <div className="mt-2 border-t border-slate-200 dark:border-slate-700 pt-3">
          <DashboardAccountDonut data={donutData} currency={currency} />
        </div>
      )}
    </div>
  );
}
